import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useRouteMatch, Link } from 'react-router-dom';
import { get } from 'lodash';
import scrollIntoView from 'scroll-into-view';
import BoardDetail from 'components/base/BoardDetail';
import SearchInput from 'components/base/SearchInput';
import {
  getAnnouncement,
  getAnnouncements,
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement,
} from 'libs/api/main';
import { usePermission, usePagination, useTimezone, useDataApi } from 'libs/hooks';
import { showNotification } from 'libs/utils';
import { TBoardDetail } from 'modules/board/types';
import { TAnnouncement } from 'modules/main/types';
import { TListResponse, TPagination, RouteMatch } from 'modules/types';
import { CWindowWidth } from 'constants/base.const';
import { ENotificationType, EOperation } from 'enums/base.enum';
import { ERoute } from 'enums/route.enum';
import { Card, Avatar, Button, Spin, Tabs, Col, Row, Typography, Collapse, List } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import BackgroundSource from 'assets/images/banners/1.webp';

const { TabPane } = Tabs;
const { Text, Title } = Typography;
const maxWidth = CWindowWidth - 300;
const IntroductionAnnouncementWrapper = styled.div`
  margin-bottom: 70px;
  color: #000;

  .title_wrapper {
    font-size: 17px;
    width: 100%;
    height: 300px;
    background-image: url(${BackgroundSource});
    background-size: cover;
    padding: 100px 10px;
    .ant-typography {
      width: 100%;
      max-width: ${maxWidth}px;
      color: #ffffff;
      margin: auto;
      display: block;
    }
  }

  .announcement-header {
    max-width: ${maxWidth}px;
    width: 100%;
    margin: 20px auto;
    font-size: 16px;
    padding: 16px;
  }

  .collapse-inline {
    font-size: 16px;
    max-width: ${maxWidth}px;
    width: 100%;
    margin: auto;
    margin-bottom: 20px;
  }

  .ant-collapse-content-box {
    width: 100%;
    min-height: 80px;
    overflow-x: hidden;
    padding: 15px 10px;
    border-radius: 8px;
    background-color: #f9f9f9;
    color: #000;
    img {
      max-width: 100%;
      height: auto;
    }
  }
  .card .ant-avatar {
    object-fit: contain;
    height: 200px;
  }
  .card .ant-card-body {
    min-height: 120px;
  }
`;

const IntroductionAnnouncement = () => {
  const { Panel } = Collapse;
  const [pagination, setPagination] = usePagination(12);
  const { adminPermission } = usePermission();
  const [listAnnouncement, setListAnnouncement] = useState<JSX.Element[]>([]);
  const reloadPage = (page?: Partial<TPagination>) => {
    setPagination({
      ...pagination,
      ...page,
    });
    setIsFirstRendering(false);
  };
  const getPromise = getAnnouncements.bind(null, {
    params: {
      current: pagination.current,
      pageSize: pagination.pageSize,
      q: pagination.q,
    },
  });
  const [{ data, loading }, setCallback] = useDataApi<TListResponse<TAnnouncement>>(getPromise, {
    contents: [],
    last: false,
  });
  const [getDateTime, timezone] = useTimezone();
  const [isFirstRendering, setIsFirstRendering] = useState(true);

  useEffect(() => {
    setCallback(() => getPromise);
  }, [pagination]);

  useEffect(() => {
    if (!loading && !isFirstRendering) {
      scrollIntoView(document?.getElementById('card-list-header') as HTMLElement, {
        align: {
          top: 0.3,
        },
      });
    }
  }, [loading]);

  async function onClickDelete(id: number) {
    try {
      await deleteAnnouncement(id);
      showNotification(ENotificationType.Success, '게시글이 삭제되었습니다.');
      reloadPage({
        current: pagination.current,
        pageSize: pagination.pageSize,
      });
    } catch (e) {
      throw e;
    }
  }

  return (
    <IntroductionAnnouncementWrapper>
      <Helmet>
        <title>공지사항</title>
      </Helmet>
      <div className='title_wrapper'>
        <Title>공지사항</Title>
        <Text style={{ marginTop: '10px', fontSize: '16px' }}> Career Step의 공지사항을 확인해주세요. </Text>
      </div>
      <Spin tip='loading...' spinning={loading} delay={1000}>
        <div className='announcement-header'>
          <Row align='middle' gutter={[0, 16]} justify='space-between'>
            <Col span={12} style={{ textAlign: 'left' }}>
              총 {get(data, 'total', 0)}개의 공지사항
            </Col>
            <Col span={12}>
              <Row justify='end' gutter={[8, 8]} id='card-list-header'>
                <Col>
                  <SearchInput
                    style={{ float: 'right' }}
                    pagination={pagination}
                    reloadPage={reloadPage}
                    placeHolder='검색어 입력'
                  />
                </Col>
                {adminPermission && (
                  <Col>
                    <Link to={`${ERoute.IntroductionAnnouncement}/${EOperation.Create}`}>
                      <Button className='add-button' type='primary'>
                        글쓰기
                      </Button>
                    </Link>
                  </Col>
                )}
              </Row>
            </Col>
          </Row>
        </div>
        <div
          style={{
            maxWidth: `${maxWidth}px`,
            margin: '20px auto',
            padding: '0px',
          }}
        >
          <List
            pagination={{
              ...pagination,
              total: get(data, 'total', 0),
              position: 'bottom',
              onChange: (page) => {
                reloadPage({
                  current: page,
                  pageSize: pagination.pageSize,
                });
              },
            }}
            className='card'
            loading={loading}
            grid={{ gutter: 16, xs: 1, sm: 2, md: 2, lg: 3, xl: 3, xxl: 3 }}
            dataSource={get(data, 'contents')}
            renderItem={(item) => (
              <List.Item>
                <Link to={`${ERoute.IntroductionAnnouncement}/${EOperation.View}/${get(item, 'id')}`}>
                  <Card hoverable cover={<Avatar shape='square' src={get(item, 'thumbnail.absolute_url')} />}>
                    <Card.Meta title={<div className='ellipsis-line'>{get(item, 'title')}</div>} />
                    <Row justify='start' style={{ position: 'absolute', bottom: '24px' }}>
                      <Col>
                        <EyeOutlined />
                        &nbsp;{get(item, 'hit_count')}
                      </Col>
                    </Row>
                  </Card>
                </Link>
              </List.Item>
            )}
          />
        </div>
      </Spin>
    </IntroductionAnnouncementWrapper>
  );
};

export const IntroductionAnnouncementDetail = () => {
  const match = useRouteMatch(`${ERoute.IntroductionAnnouncement}/:operation/:record_id?`);
  let { operation = EOperation.View, record_id } = (match?.params as RouteMatch) || {};
  const [{ data, loading }] = useDataApi<TBoardDetail>(
    getAnnouncement.bind(null, record_id),
    {},
    operation != EOperation.Create,
  );

  return (
    <BoardDetail
      operation={operation}
      pathName={ERoute.IntroductionAnnouncement}
      promiseCreate={createAnnouncement}
      promiseDelete={deleteAnnouncement}
      promiseUpdate={updateAnnouncement}
      record={data}
      loading={loading}
      useFile={false}
      useThumbnail={true}
    />
  );
};

export default IntroductionAnnouncement;
